import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Search,
  X,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  MapPin,
  Truck,
  Package,
  ScanLine,
  FileText,
  Clock,
  ChevronRight,
  Loader2,
  Volume2,
  VolumeX,
  Settings,
  Filter,
  Thermometer,
  Shield,
  Users,
  Receipt,
  Wallet,
  Archive,
  RefreshCw,
  Trash2,
  Wrench,
  BellOff,
  BellRing } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { ScrollArea, ScrollBar } from '@/shared/components/ui/scroll-area';
import { Switch } from '@/shared/components/ui/switch';
import { Label } from '@/shared/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger } from
'@/shared/components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem } from
'@/shared/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger } from
'@/shared/components/ui/popover';
import { toast } from 'sonner';

const API_URL = (API_BASE || '').replace(/\/$/, '');

// Severity configurations
const SEVERITY_CONFIG = {
  CRITICAL: { color: 'bg-red-500', icon: AlertCircle, textColor: 'text-red-500' },
  HIGH: { color: 'bg-orange-500', icon: AlertTriangle, textColor: 'text-orange-500' },
  MEDIUM: { color: 'bg-yellow-500', icon: AlertTriangle, textColor: 'text-yellow-500' },
  LOW: { color: 'bg-blue-500', icon: Info, textColor: 'text-blue-500' },
  INFO: { color: 'bg-slate-500', icon: Info, textColor: 'text-slate-500' }
};

// Event type icons
const EVENT_ICONS = {
  'GEOFENCE': MapPin,
  'VEHICLE': Truck,
  'PACKAGE': Package,
  'SCAN': ScanLine,
  'DELIVERY': FileText,
  'COLDCHAIN': Thermometer,
  'TEMPERATURE': Thermometer,
  'ACCESS': Shield,
  'USER': Users,
  'FINANCE': Receipt,
  'PAYMENT': Wallet,
  'INVENTORY': Archive,
  'FLEET': Truck,
  'FUEL': Truck,
  'TIRE': Truck,
  'MAINTENANCE': Wrench,
  'LOW_STOCK': Archive,
  'DEFAULT': Bell
};

// Category configurations
const getCategoryConfig = (t) => ({
  all: { label: t('notifications.all', 'All'), icon: Bell, color: 'text-slate-500' },
  critical: { label: t('notifications.critical', 'Critical'), icon: AlertCircle, color: 'text-red-500' },
  operations: { label: t('notifications.operations', 'Operations'), icon: Package, color: 'text-blue-500' },
  logistics: { label: t('notifications.logistics', 'Logistics'), icon: Truck, color: 'text-amber-500' },
  fleet: { label: t('notifications.fleet', 'Fleet'), icon: Truck, color: 'text-red-500' },
  geofence: { label: t('notifications.geofence', 'Geofence'), icon: MapPin, color: 'text-green-500' },
  coldchain: { label: t('notifications.coldchain', 'Cold Chain'), icon: Thermometer, color: 'text-cyan-500' },
  security: { label: t('notifications.security', 'Security'), icon: Shield, color: 'text-violet-500' },
  finance: { label: t('notifications.finance', 'Finance'), icon: Receipt, color: 'text-emerald-500' }
});

const getEventIcon = (eventType) => {
  if (!eventType) return EVENT_ICONS.DEFAULT;
  for (const [key, icon] of Object.entries(EVENT_ICONS)) {
    if (eventType.toUpperCase().includes(key)) return icon;
  }
  return EVENT_ICONS.DEFAULT;
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const NotificationCenter = () => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [archivedNotifications, setArchivedNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('notification_sound') !== 'false';
  });
  const [wsConnected, setWsConnected] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('notification_preferences');
    return saved ? JSON.parse(saved) : {
      showCritical: true,
      showHigh: true,
      showMedium: true,
      showLow: true,
      showInfo: true,
      categories: {
        operations: true,
        logistics: true,
        fleet: true,
        geofence: true,
        coldchain: true,
        security: true,
        finance: true
      },
      desktopNotifications: false,
      emailDigest: 'none' // none, daily, weekly
    };
  });
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Save preferences
  useEffect(() => {
    localStorage.setItem('notification_preferences', JSON.stringify(preferences));
  }, [preferences]);

  useEffect(() => {
    localStorage.setItem('notification_sound', soundEnabled.toString());
  }, [soundEnabled]);

  // Request desktop notification permission
  const requestDesktopPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPreferences((p) => ({ ...p, desktopNotifications: true }));
        toast.success('Desktop notifications enabled');
      }
    }
  };

  // Show desktop notification
  const showDesktopNotification = (notification) => {
    if (preferences.desktopNotifications && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title || 'EzERP Alert', {
        body: notification.message,
        icon: '/logo192.png',
        tag: notification.id
      });
    }
  };

  // Get notification category
  const getNotificationCategory = (notification) => {
    const type = (notification.type || notification.event_type || '').toLowerCase();
    if (type.includes('fleet') || type.includes('fuel') || type.includes('tire') || type.includes('maintenance') || type.includes('low_stock')) return 'fleet';
    if (type.includes('geofence') || type.includes('zone')) return 'geofence';
    if (type.includes('coldchain') || type.includes('temperature')) return 'coldchain';
    if (type.includes('delivery') || type.includes('route') || type.includes('vehicle')) return 'logistics';
    if (type.includes('scan') || type.includes('pick') || type.includes('pack')) return 'operations';
    if (type.includes('access') || type.includes('login') || type.includes('security')) return 'security';
    if (type.includes('payment') || type.includes('invoice') || type.includes('finance')) return 'finance';
    return 'operations';
  };

  // Check if notification passes filters
  const passesFilters = (notification) => {
    // Check severity filter
    const severity = notification.severity || 'INFO';
    if (severity === 'CRITICAL' && !preferences.showCritical) return false;
    if (severity === 'HIGH' && !preferences.showHigh) return false;
    if (severity === 'MEDIUM' && !preferences.showMedium) return false;
    if (severity === 'LOW' && !preferences.showLow) return false;
    if (severity === 'INFO' && !preferences.showInfo) return false;

    // Check category filter
    const category = getNotificationCategory(notification);
    if (!preferences.categories[category]) return false;

    return true;
  };

  // Archive notification
  const archiveNotification = (notificationId) => {
    const notification = [...liveAlerts, ...notifications].find((n) => n.id === notificationId);
    if (notification) {
      setArchivedNotifications((prev) => [notification, ...prev.slice(0, 99)]);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setLiveAlerts((prev) => prev.filter((n) => n.id !== notificationId));
    }
  };

  // Load notifications from API
  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('ezerpToken');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/trails/notifications?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'anonymous';
    const userRole = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.role || 'operator') || 'operator';

    // Use the correct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Normalize host to handle cases where API_URL might be a relative path or have a scheme
    let wsHost = API_URL.replace(/^https?:\/\//, '');
    if (!wsHost) {
      wsHost = window.location.host;
    }

    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/notifications?user_id=${userId}&role=${userRole}`;

    try {
      console.log(`[NotificationCenter] Connecting to WebSocket: ${wsUrl}`);
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[NotificationCenter] WebSocket connection established');
        setWsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('[NotificationCenter] Failed to parse message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log(`[NotificationCenter] WebSocket disconnected. Code: ${event.code}, Reason: ${event.reason || 'none'}`);
        setWsConnected(false);
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('[NotificationCenter] WebSocket encountered error:', error);
      };
    } catch (error) {
      console.error('[NotificationCenter] Failed to initiate WebSocket connection:', error);
      // Retry connection
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    }
  }, []);

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (message) => {
    if (message.type === 'connected' || message.type === 'pong') return;

    // Check if passes filters before showing
    if (!passesFilters(message)) return;

    // Add to live alerts
    const alert = {
      id: `live-${Date.now()}`,
      ...message,
      isLive: true
    };

    setLiveAlerts((prev) => [alert, ...prev.slice(0, 49)]);

    // Show toast for critical/high severity
    if (message.severity === 'CRITICAL' || message.severity === 'HIGH') {
      toast.error(message.title || 'Alert', {
        description: message.message,
        duration: 10000
      });

      // Play sound if enabled
      if (soundEnabled) {
        playAlertSound();
      }

      // Show desktop notification
      showDesktopNotification(message);
    } else if (message.type === 'notification') {
      toast.info(message.title || 'Notification', {
        description: message.message,
        duration: 5000
      });
    }
  };

  const playAlertSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.gain.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {

      // Audio not available
    }};

  // Mark notifications as read
  const markAsRead = async (notificationIds) => {
    try {
      const token = localStorage.getItem('ezerpToken');
      if (!token) return;

      await fetch(`${API_URL}/api/trails/notifications/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(notificationIds)
      });

      setNotifications((prev) =>
      prev.filter((n) => !notificationIds.includes(n.id))
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = () => {
    const ids = notifications.map((n) => n.id);
    if (ids.length > 0) {
      markAsRead(ids);
    }
    setLiveAlerts([]);
  };

  // Filter notifications
  const filteredNotifications = [...liveAlerts, ...notifications].filter((n) => {
    // Check preferences filter
    if (!passesFilters(n)) return false;

    // Check search
    const matchesSearch = !searchQuery ||
    n.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.message?.toLowerCase().includes(searchQuery.toLowerCase());

    // Check tab filter
    const category = getNotificationCategory(n);
    const matchesTab = activeTab === 'all' ||
    activeTab === 'critical' && (n.severity === 'CRITICAL' || n.severity === 'HIGH') ||
    activeTab === 'fleet' && category === 'fleet' ||
    activeTab === 'operations' && category === 'operations' ||
    activeTab === 'logistics' && category === 'logistics' ||
    activeTab === 'geofence' && category === 'geofence' ||
    activeTab === 'coldchain' && category === 'coldchain' ||
    activeTab === 'security' && category === 'security' ||
    activeTab === 'finance' && category === 'finance';

    return matchesSearch && matchesTab;
  });

  const unreadCount = liveAlerts.length + notifications.length;
  const criticalCount = [...liveAlerts, ...notifications].filter(
    (n) => n.severity === 'CRITICAL' || n.severity === 'HIGH'
  ).length;

  // Effects
  useEffect(() => {
    loadNotifications();
    connectWebSocket();

    // Refresh every 30 seconds
    const interval = setInterval(loadNotifications, 30000);

    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [loadNotifications, connectWebSocket]);

  // Send ping every 30 seconds to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, []);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:text-foreground"
          data-testid="notification-bell">
          
          <Bell className="h-5 w-5" />
          {unreadCount > 0 &&
          <Badge
            variant={criticalCount > 0 ? "destructive" : "default"}
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            data-testid="notification-badge">
            
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          }
          {wsConnected &&
          <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-green-500 rounded-full" />
          }
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:w-[450px] p-0" data-testid="notification-center">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />{t("notifications", "Notifications")}

              {wsConnected &&
              <span className="text-xs text-green-500 font-normal">{t("live", "\u25CF Live")}</span>
              }
            </SheetTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="h-8 w-8"
                title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}>
                
                {soundEnabled ?
                <Volume2 className="h-4 w-4" /> :

                <VolumeX className="h-4 w-4 text-muted-foreground" />
                }
              </Button>

              {/* Settings Popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Settings className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm">{t("notificationPreferences", "Notification Preferences")}</h4>

                    {/* Severity Filters */}
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">{t("severityLevels", "Severity Levels")}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                        { key: 'showCritical', label: 'Critical', color: 'text-red-500' },
                        { key: 'showHigh', label: 'High', color: 'text-orange-500' },
                        { key: 'showMedium', label: 'Medium', color: 'text-yellow-500' },
                        { key: 'showLow', label: 'Low', color: 'text-blue-500' }].
                        map(({ key, label, color }) =>
                        <div key={key} className="flex items-center justify-between">
                            <Label htmlFor={key} className={`text-xs ${color}`}>{label}</Label>
                            <Switch
                            id={key}
                            checked={preferences[key]}
                            onCheckedChange={(checked) =>
                            setPreferences((p) => ({ ...p, [key]: checked }))
                            } />
                          
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Category Filters */}
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">{t("categories", "Categories")}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(getCategoryConfig(t)).filter(([k]) => k !== 'all' && k !== 'critical').map(([key, config]) => {
                          const Icon = config.icon;
                          return (
                            <div key={key} className="flex items-center justify-between">
                              <Label htmlFor={`cat-${key}`} className={`text-xs flex items-center gap-1 ${config.color}`}>
                                <Icon className="h-3 w-3" />
                                {config.label}
                              </Label>
                              <Switch
                                id={`cat-${key}`}
                                checked={preferences.categories[key]}
                                onCheckedChange={(checked) =>
                                setPreferences((p) => ({
                                  ...p,
                                  categories: { ...p.categories, [key]: checked }
                                }))
                                } />
                              
                            </div>);

                        })}
                      </div>
                    </div>

                    {/* Desktop Notifications */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <Label htmlFor="desktop" className="text-xs flex items-center gap-1">
                        <BellRing className="h-3 w-3" />{t("desktopNotifications", "Desktop Notifications")}

                      </Label>
                      <Switch
                        id="desktop"
                        checked={preferences.desktopNotifications}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            requestDesktopPermission();
                          } else {
                            setPreferences((p) => ({ ...p, desktopNotifications: false }));
                          }
                        }} />
                      
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {unreadCount > 0 &&
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">{t("markAllRead", "Mark all read")}

              </Button>
              }
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="notification-search" />
            
          </div>
        </SheetHeader>

        {/* Tabs - Scrollable for more categories */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <ScrollArea className="w-full">
            <TabsList className="w-max px-4 py-2 bg-muted/50 flex gap-1">
              <TabsTrigger value="all" className="text-xs px-3">
                <Bell className="h-3 w-3 mr-1" />{t("all", "All (")}
                {unreadCount})
              </TabsTrigger>
              <TabsTrigger value="critical" className="text-xs px-3">
                <AlertCircle className="h-3 w-3 mr-1 text-red-500" />{t("critical", "Critical (")}
                {criticalCount})
              </TabsTrigger>
              <TabsTrigger value="fleet" className="text-xs px-3">
                <Truck className="h-3 w-3 mr-1 text-red-500" />{t("fleet", "Fleet")}

              </TabsTrigger>
              <TabsTrigger value="operations" className="text-xs px-3">
                <Package className="h-3 w-3 mr-1" />{t("ops", "Ops")}

              </TabsTrigger>
              <TabsTrigger value="logistics" className="text-xs px-3">
                <Truck className="h-3 w-3 mr-1" />{t("logistics", "Logistics")}

              </TabsTrigger>
              <TabsTrigger value="geofence" className="text-xs px-3">
                <MapPin className="h-3 w-3 mr-1" />{t("geo", "Geo")}

              </TabsTrigger>
              <TabsTrigger value="coldchain" className="text-xs px-3">
                <Thermometer className="h-3 w-3 mr-1" />{t("cold", "Cold")}

              </TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <TabsContent value={activeTab} className="m-0">
            <ScrollArea className="h-[calc(100vh-220px)]">
              {isLoading ?
              <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div> :
              filteredNotifications.length === 0 ?
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mb-4 opacity-50" />
                  <p className="text-sm">{t("allCaughtUp", "All caught up!")}</p>
                  <p className="text-xs">{t("noNotificationsTo", "No notifications to show")}</p>
                </div> :

              <div className="divide-y">
                  {filteredNotifications.map((notification) => {
                  const severity = SEVERITY_CONFIG[notification.severity] || SEVERITY_CONFIG.INFO;
                  const SeverityIcon = severity.icon;
                  const EventIcon = getEventIcon(notification.event_type);

                  return (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${notification.isLive ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`
                      }
                      onClick={() => {
                        if (notification.payload?.route_id) {
                          navigate(`/wms/trails?route=${notification.payload.route_id}`);
                        } else if (notification.payload?.zone_id) {
                          navigate('/wms/geofencing');
                        }
                        if (!notification.isLive && notification.id) {
                          markAsRead([notification.id]);
                        }
                        setIsOpen(false);
                      }}
                      data-testid={`notification-item-${notification.id}`}>
                      
                        <div className="flex gap-3">
                          {/* Severity indicator */}
                          <div className={`h-2 w-2 rounded-full mt-2 ${severity.color}`} />

                          {/* Icon */}
                          <div className={`p-2 rounded-lg bg-muted ${severity.textColor}`}>
                            <EventIcon className="h-4 w-4" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-medium text-sm truncate">
                                {notification.title || notification.event_type || 'Notification'}
                              </p>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                                <Clock className="h-3 w-3" />
                                {formatTimeAgo(notification.timestamp || notification.created_at)}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {notification.message}
                            </p>

                            {/* Tags */}
                            <div className="flex items-center gap-2 mt-2">
                              {notification.severity &&
                            <Badge variant="outline" className={`text-xs ${severity.textColor}`}>
                                  {notification.severity}
                                </Badge>
                            }
                              {notification.isLive &&
                            <Badge variant="secondary" className="text-xs">{t("live", "Live")}

                            </Badge>
                            }
                              {notification.type === 'geofence_alert' &&
                            <Badge variant="outline" className="text-xs">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {notification.zone_name}
                                </Badge>
                            }
                            </div>
                          </div>

                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-2" />
                        </div>
                      </div>);

                })}
                </div>
              }
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-background">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              navigate('/wms/trails');
              setIsOpen(false);
            }}>
            
            {t('notifications.viewAll', 'View All Activity')}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>);

};

export default NotificationCenter;
